import { downloadBlob } from '@risksmart-app/components/src/file/fileUtils';
import type { FileType } from '@risksmart-app/components/src/file/types';
import axios from 'axios';

import { useTRPCClient } from '@/utils/trpc';

// Bare axios client for the presigned S3 GET: no app Authorization header,
// and — unlike the shared `unauthorisedAxiosInstance` — no Sentry response
// interceptor, so a failed request can't ship the signed URL (signature,
// credential params) to an error tracker we are decommissioning anyway.
const s3Client = axios.create();

const getFileFromS3 = async (url: string) =>
  (await s3Client.get<Blob>(url, { responseType: 'blob' })).data;

/**
 * tRPC twin of `@risksmart-app/components/src/file/useFileDownload` (V2:
 * REST `GET /files/:id` → S3 GET). Same callable signature; the download
 * URL comes from `files.getDownloadUrl`, which permission-gates via the
 * data-layer file read before issuing the presigned URL.
 *
 * Consumers must import the flag-gated `useFileDownload` wrapper, never
 * this.
 */
export const useFileDownloadTRPC = () => {
  const trpcClient = useTRPCClient();

  return async (
    { fileId, fileName, file }: FileType,
    downloadFile = true
  ): Promise<Blob> => {
    const getFileBlob = async (id: string) => {
      const { url } = await trpcClient.frontend.files.getDownloadUrl.query({
        fileId: id,
      });

      return await getFileFromS3(url);
    };

    const blob = file || (fileId && (await getFileBlob(fileId)));
    if (!blob) {
      throw new Error('No file or fileId to download');
    }
    if (downloadFile) {
      downloadBlob(fileName, blob);
    }

    return blob;
  };
};
